import { cn } from '@amore/ui/cn'
import { Alert } from '@amore/ui/components/alert'
import { Badge } from '@amore/ui/components/badge'
import { Input } from '@amore/ui/components/input'
import { Select, SelectItem } from '@amore/ui/components/select'
import { Textarea } from '@amore/ui/components/textarea'
import { TimePicker } from '@amore/ui/components/time-picker'
import {
  Clock3,
  Eye,
  Flag,
  type LucideIcon,
  MapPin,
  NotebookPen,
  Trash2,
  TriangleAlert,
  UserRound,
} from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { newId } from '@/lib/format'
import { supplierCategoryLabels, supplierStatusLabels, supplierStatusTone } from '@/lib/labels'
import { useCanEdit, useUpdate, useWedding } from '@/mock/store'
import type { ScheduleAudience, ScheduleEvent, ScheduleOwner } from '@/mock/types'
import { Segmented } from '@/patterns/detail'
import { ConfirmDialog, DetailSheet } from '@/patterns/dialog'
import { type LinkedModule, LinkedRecord } from '@/patterns/linked-record'
import { useToast } from '@/shell/toaster'
import {
  durationLabel,
  isConflict,
  placeEvent,
  scheduleGaps,
  scheduleGroups,
  suggestDayPart,
  timeRange,
} from './model'

const audiences: { value: ScheduleAudience; label: string }[] = [
  { value: 'guests', label: 'Hosté i tým' },
  { value: 'team', label: 'Jen tým' },
]

const timeLabels = {
  open: 'Vybrat čas',
  title: 'Vybrat čas',
  close: 'Zavřít výběr času',
  hours: 'Hodiny',
  minutes: 'Minuty',
  clear: 'Bez času',
  confirm: 'Potvrdit čas',
  format: '24hodinový formát · HH:mm',
}

const sectionModules: Record<string, LinkedModule> = {
  ceremony: 'ceremony',
  reception: 'reception',
  photo: 'photo',
  music: 'music',
  beauty: 'beauty',
  decor: 'decor',
  venue: 'venue',
}

const softControl = 'h-10 border-transparent bg-muted/60 hover:bg-muted'
const none = 'none'
const preparations = 'preparations'

type Draft = Pick<
  ScheduleEvent,
  'title' | 'time' | 'endTime' | 'location' | 'audience' | 'dayPartId' | 'description' | 'owner'
>

function draftOf(event: ScheduleEvent): Draft {
  const { title, time, endTime, location, audience, dayPartId, description, owner } = event
  return { title, time, endTime, location, audience, dayPartId, description, owner }
}

const ownerValue = (owner: ScheduleOwner | undefined) =>
  owner ? `${owner.type}:${owner.id}` : none

function parseOwner(value: string): ScheduleOwner | undefined {
  const [type, id] = value.split(':')
  return (type === 'member' || type === 'supplier') && id ? { type, id } : undefined
}

/**
 * One sheet for adding and editing an event (no dialogs): the name as the
 * heading, the properties with live controls, a clash warning, where the
 * event comes from and who handles it, and remove/delete in the footer.
 * Without `event` it adds one, prefilled from `initial`.
 */
export function EventSheet({
  event: existing,
  initial,
  onClose,
}: {
  event?: ScheduleEvent
  /** Prefills a new event, e.g. from "Na co nezapomenout". */
  initial?: Partial<ScheduleEvent>
  onClose: () => void
}) {
  const isNew = !existing
  const event: ScheduleEvent = existing ?? {
    id: 'new',
    time: null,
    endTime: null,
    title: '',
    description: '',
    location: '',
    audience: 'team',
    dayPartId: null,
    removed: false,
    ...initial,
  }
  const data = useWedding()
  const canEdit = useCanEdit()
  const update = useUpdate()
  const { showToast } = useToast()
  const [draft, setDraft] = useState(() => draftOf(event))
  // A new event follows its time into a part of the day until the couple (or `initial`) picks one.
  const [partTouched, setPartTouched] = useState(!isNew || initial?.dayPartId !== undefined)
  const [titleError, setTitleError] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const set = (patch: Partial<Draft>) => setDraft((current) => ({ ...current, ...patch }))
  const dirty = JSON.stringify(draft) !== JSON.stringify(draftOf(event))

  const groups = scheduleGroups(data)
  const preview = { ...event, ...draft, id: isNew ? 'draft' : event.id }
  const gap = scheduleGaps(
    scheduleGroups({ ...data, schedule: placeEvent(data.schedule, preview) }),
  ).get(preview.id)
  const clash = isConflict(gap) ? gap : undefined
  const sectionModule = event.source ? sectionModules[event.source.section] : undefined
  const supplier =
    draft.owner?.type === 'supplier'
      ? data.suppliers.find((item) => item.id === draft.owner?.id)
      : undefined
  const suppliers = data.suppliers.filter(
    (item) => item.status !== 'cancelled' || item.id === draft.owner?.id,
  )
  const locations = [...new Set(data.schedule.map((item) => item.location).filter(Boolean))].sort(
    (a, b) => a.localeCompare(b, 'cs'),
  )
  const duration = durationLabel(draft.time, draft.endTime)

  const setTime = (time: string | null) => {
    const suggested = !partTouched && time ? suggestDayPart(groups, time) : undefined
    set({ time, ...(suggested !== undefined ? { dayPartId: suggested } : {}) })
  }
  const save = () => {
    if (!draft.title.trim()) {
      setTitleError(true)
      return
    }
    const saved: ScheduleEvent = {
      ...event,
      ...draft,
      title: draft.title.trim(),
      location: draft.location.trim(),
      id: isNew ? newId('event') : event.id,
    }
    update((current) => ({ ...current, schedule: placeEvent(current.schedule, saved) }))
    showToast({
      title: isNew ? 'Událost přidána' : 'Událost uložena',
      description: saved.title,
      tone: 'success',
    })
    onClose()
  }
  const removeFromSchedule = () => {
    const restore = () =>
      update((current) => ({
        ...current,
        schedule: current.schedule.map((item) =>
          item.id === event.id ? { ...item, removed: false } : item,
        ),
      }))
    update((current) => ({
      ...current,
      schedule: current.schedule.map((item) =>
        item.id === event.id ? { ...item, removed: true } : item,
      ),
    }))
    showToast({
      title: 'Událost odebrána',
      description: `${event.title} je v odebraných, sekce ${event.source?.label} zůstává beze změny.`,
      action: { label: 'Vrátit zpět', altText: 'Vrátit událost do harmonogramu', onClick: restore },
    })
    onClose()
  }
  const remove = () => {
    update((current) => ({
      ...current,
      schedule: current.schedule.filter((item) => item.id !== event.id),
    }))
    showToast({ title: 'Událost smazána', description: event.title })
    onClose()
  }

  return (
    <>
      <DetailSheet
        open
        onOpenChange={(open) => {
          if (!open) onClose()
        }}
        eyebrow={isNew ? 'Nová událost' : 'Událost'}
        title={event.title || 'Nová událost'}
        titleContent={
          <div className="space-y-1">
            <textarea
              value={draft.title}
              rows={1}
              data-autofocus={isNew || undefined}
              readOnly={!canEdit}
              aria-label="Název události"
              aria-invalid={titleError || undefined}
              placeholder={isNew ? 'Co se bude dít?' : 'Název události'}
              onChange={(change) => {
                set({ title: change.target.value })
                setTitleError(false)
              }}
              className={cn(
                'field-sizing-content -mx-2 w-[calc(100%+1rem)] resize-none rounded-lg bg-transparent px-2 py-1 font-heading text-[1.75rem] leading-tight tracking-tight outline-none transition-colors',
                canEdit && 'hover:bg-muted/60 focus:bg-muted/60',
              )}
            />
            {titleError ? (
              <p className="text-sm text-error-foreground">Doplňte název události.</p>
            ) : null}
          </div>
        }
        dirty={dirty}
        primaryAction={
          canEdit ? { label: isNew ? 'Přidat událost' : 'Uložit', onClick: save } : undefined
        }
        destructiveAction={
          canEdit && !isNew
            ? event.source
              ? {
                  label: 'Odebrat z harmonogramu',
                  icon: <Trash2 className="size-4" aria-hidden="true" />,
                  onClick: removeFromSchedule,
                }
              : {
                  label: 'Smazat událost',
                  icon: <Trash2 className="size-4" aria-hidden="true" />,
                  onClick: () => setConfirmDelete(true),
                }
            : undefined
        }
      >
        <div className="divide-y divide-border [&>*]:py-7 [&>*:first-child]:pt-1 [&>*:last-child]:pb-1">
          {clash ? (
            <Alert tone="warning" announcement="none" className="flex items-start gap-2.5">
              <TriangleAlert className="mt-1 size-4 shrink-0" aria-hidden="true" />
              {clash.type === 'overlap'
                ? `Překrývá se s událostí ${clash.with.title} (${timeRange(clash.with)}). ${
                    clash.with.audience === 'guests' && draft.audience === 'guests'
                      ? 'Hosté nemůžou být na dvou místech najednou.'
                      : 'Stejný člověk nemůže být na dvou místech najednou.'
                  }`
                : `Začíná dřív než předchozí událost ${clash.with.title} (${clash.with.time}). Posuňte ji v harmonogramu, nebo upravte čas.`}
            </Alert>
          ) : null}

          <fieldset disabled={!canEdit} className="grid grid-cols-2 gap-x-6 gap-y-7">
            <legend className="sr-only">Vlastnosti události</legend>
            <Tile icon={Clock3} label="Začátek">
              <TimePicker
                value={draft.time ?? ''}
                onValueChange={(value) => setTime(value || null)}
                readOnly={!canEdit}
                labels={timeLabels}
                placeholder="Bez času"
                className={softControl}
              />
            </Tile>
            <Tile icon={Clock3} label="Konec">
              <TimePicker
                value={draft.endTime ?? ''}
                onValueChange={(value) => set({ endTime: value || null })}
                readOnly={!canEdit}
                labels={timeLabels}
                placeholder="Neurčeno"
                className={softControl}
              />
              {duration ? (
                <span className="block px-1 text-xs text-muted-foreground">{duration}</span>
              ) : null}
            </Tile>

            <Tile icon={Flag} label="Část dne">
              <Select
                variant="soft"
                className={softControl}
                value={draft.dayPartId ?? preparations}
                onValueChange={(value) => {
                  setPartTouched(true)
                  set({ dayPartId: value === preparations ? null : value })
                }}
              >
                <SelectItem value={preparations}>Přípravy</SelectItem>
                {data.dayParts.map((part) => (
                  <SelectItem key={part.id} value={part.id}>
                    {part.label}
                  </SelectItem>
                ))}
              </Select>
            </Tile>
            <Tile icon={Eye} label="Kdo uvidí">
              <Segmented
                label="Kdo uvidí"
                options={audiences}
                value={draft.audience}
                onChange={(audience) => set({ audience })}
                disabled={!canEdit}
              />
              <span className="block px-1 text-xs text-muted-foreground">
                {draft.audience === 'guests'
                  ? 'Objeví se v programu na svatebním webu.'
                  : 'Hosté ji na webu neuvidí.'}
              </span>
            </Tile>

            <Tile icon={MapPin} label="Místo" wide>
              <Input
                variant="soft"
                value={draft.location}
                list="schedule-locations"
                readOnly={!canEdit}
                placeholder={canEdit ? 'Např. Zámecká zahrada' : 'Bez místa'}
                onChange={(change) => set({ location: change.target.value })}
                className={softControl}
              />
              <datalist id="schedule-locations">
                {locations.map((location) => (
                  <option key={location} value={location} />
                ))}
              </datalist>
            </Tile>

            <Tile icon={UserRound} label="Zajišťuje" wide>
              <Select
                variant="soft"
                className={softControl}
                value={ownerValue(draft.owner)}
                onValueChange={(value) => set({ owner: parseOwner(value) })}
              >
                <SelectItem value={none}>Nikdo</SelectItem>
                {data.members.map((member) => (
                  <SelectItem key={member.id} value={`member:${member.id}`}>
                    {member.name}
                  </SelectItem>
                ))}
                {suppliers.map((item) => (
                  <SelectItem key={item.id} value={`supplier:${item.id}`}>
                    {item.name} · {supplierCategoryLabels[item.category] ?? item.category}
                  </SelectItem>
                ))}
              </Select>
            </Tile>

            <Tile icon={NotebookPen} label="Popis" wide>
              <Textarea
                value={draft.description}
                maxLength={3000}
                readOnly={!canEdit}
                placeholder={
                  canEdit ? 'Co mají vědět hosté nebo tým, plán B při dešti…' : 'Bez popisu'
                }
                onChange={(change) => set({ description: change.target.value })}
                className="min-h-24 border-transparent bg-muted/60 hover:bg-muted focus:bg-card"
              />
            </Tile>
          </fieldset>

          {supplier ? (
            <section className="space-y-2.5">
              <h3 className="text-sm font-semibold">Zajišťuje dodavatel</h3>
              <LinkedRecord
                module="suppliers"
                title={supplier.name}
                meta={[
                  supplierCategoryLabels[supplier.category] ?? supplier.category,
                  supplier.contacts[0]?.phone,
                ]
                  .filter(Boolean)
                  .join(' · ')}
                status={
                  <Badge tone={supplierStatusTone(supplier.status)}>
                    {supplierStatusLabels[supplier.status]}
                  </Badge>
                }
              />
            </section>
          ) : null}

          {event.source ? (
            <section className="space-y-2.5">
              <h3 className="text-sm font-semibold">Pochází ze sekce</h3>
              {sectionModule ? (
                <LinkedRecord
                  module={sectionModule}
                  title={event.source.label}
                  meta={
                    event.source.section === 'ceremony'
                      ? 'Čas a prostor se zobrazují i v přehledu obřadu'
                      : 'Úpravy tady sekci nezmění'
                  }
                />
              ) : (
                <p className="text-sm text-muted-foreground">{event.source.label}</p>
              )}
            </section>
          ) : null}
        </div>
      </DetailSheet>
      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        heading="Smazat událost?"
        description={`${event.title} je vaše vlastní událost, smazáním zmizí z harmonogramu i z webu.`}
        keepLabel="Ponechat událost"
        confirmLabel="Smazat událost"
        onConfirm={remove}
      />
    </>
  )
}

/** One property: its label above, its control always live (no extra click to edit). */
function Tile({
  icon: Icon,
  label,
  wide = false,
  children,
}: {
  icon: LucideIcon
  label: string
  wide?: boolean
  children: ReactNode
}) {
  return (
    <div className={cn('space-y-2.5', wide && 'col-span-2')}>
      <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
        <Icon className="size-4" aria-hidden="true" />
        {label}
      </span>
      <div className="space-y-1">{children}</div>
    </div>
  )
}
